import { GetPayUpsResponseType } from '@orbit/api-types'
import api from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import { useMutation, useQueryClient } from '@tanstack/react-query'

interface DeletePayUpVariables {
    tripId: string
    payUpId: number
}

interface DeletePayUpContext {
    snapshot: GetPayUpsResponseType | undefined
}

export const useDeletePayUp = () => {
    const queryClient = useQueryClient()

    return useMutation<void, Error, DeletePayUpVariables, DeletePayUpContext>({
        mutationFn: async ({ tripId, payUpId }) => {
            await api.delete(`/trips/${tripId}/payUps/${payUpId}`)
        },
        onMutate: async ({ tripId, payUpId }) => {
            const queryKey = queryKeys.payups(tripId)
            await queryClient.cancelQueries({ queryKey })
            const snapshot = queryClient.getQueryData<GetPayUpsResponseType>(queryKey)
            queryClient.setQueryData<GetPayUpsResponseType>(queryKey, prev => {
                if (!prev) return prev
                return { payUps: prev.payUps.filter(p => p.id !== payUpId) }
            })
            return { snapshot }
        },
        onError: (_err, variables, context) => {
            if (context) {
                queryClient.setQueryData(queryKeys.payups(variables.tripId), context.snapshot)
            }
        },
        onSettled: (_data, _err, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.payups(variables.tripId) })
            queryClient.invalidateQueries({ queryKey: queryKeys.paymentsOwed(variables.tripId) })
            queryClient.invalidateQueries({ queryKey: queryKeys.expenses(variables.tripId) })
        },
    })
}
